/**
 * LegislatorTable Component
 * 
 * Shared table display for legislators with configurable columns.
 * Used by LegislatorsPage, LegislatorsTab, and LegislatorsReport.
 */

import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Star, 
  Trash2, 
  Eye, 
  Bookmark,
  Mail,
  Phone,
  Globe,
  FileText,
  Vote,
  ExternalLink,
  StickyNote
} from 'lucide-react';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import Chip from '../Chip';
import TruncatedText from '../TruncatedText';
import { BaseLegislator, TrackedLegislator, SortField, SortDirection, LegislatorRenderContext } from './types';
import { getPartyTextColor, formatPartyDisplay } from '../../utils/partyUtils';
import { getLegislatorLinkClasses } from '../../utils/linkStyles';
import { formatLegislatorNameFromObject, formatPhoneNumber } from '../../utils/nameUtils';
import { getPositionColorClasses } from '../../utils/positionColors';
import { createBillFilterUrl } from '../../utils/billStatusMapping';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import DistrictMapLink from '../DistrictMapLink';
import { padDistrictNumber } from '../../utils/districtDisplay';

// =============================================================================
// Types
// =============================================================================

export type LegislatorTableColumn = 
  | 'checkbox'
  | 'name'
  | 'chamber'
  | 'district'
  | 'party'
  | 'committees'
  | 'contact'
  | 'position'
  | 'notes'
  | 'sponsored_bills'
  | 'votes'
  | 'actions'
  | 'workspaces';

interface LegislatorTableProps<T extends BaseLegislator = BaseLegislator> {
  /** Data to display */
  data: T[];
  /** Loading state */
  isLoading?: boolean;
  /** Columns to display (in order) */
  columns: LegislatorTableColumn[];
  /** Sort state */
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: string) => void;
  /** Context for rendering (callbacks, permissions, etc.) */
  context: LegislatorRenderContext;
  /** Table variant */
  variant?: 'default' | 'compact' | 'report';
  /** For report view styling */
  isReportView?: boolean;
  /** Custom row click handler */
  onRowClick?: (legislator: T) => void;
  /** Custom render for notes column */
  renderNotesCell?: (legislator: T) => React.ReactNode;
}

// =============================================================================
// Helper Functions
// =============================================================================

function getLegislatorId(legislator: BaseLegislator): number {
  // Data is normalized to camelCase, but keep fallback for safety
  return legislator.id || 0;
}

function getLegislatorLink(
  legislatorId: number,
  context: LegislatorRenderContext,
  hash?: string
): string {
  // Use public links if explicitly requested or if in public view mode
  const usePublic = context.usePublicLinks || context.isPublicView;
  const basePath = usePublic ? '/public/legislators' : '/legislators';
  const params = !usePublic && context.workspaceId 
    ? `?workspace=${context.workspaceId}` 
    : '';
  return `${basePath}/${legislatorId}${params}${hash ? `#${hash}` : ''}`;
}

// =============================================================================
// Component
// =============================================================================

function LegislatorTable<T extends BaseLegislator = BaseLegislator>({
  data,
  isLoading = false,
  columns,
  sortField,
  sortDirection,
  onSort,
  context,
  variant = 'default',
  isReportView = false,
  onRowClick,
  renderNotesCell,
}: LegislatorTableProps<T>): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  
  const tableVariant = isReportView ? 'report' : variant;

  // Render column header
  const renderHeader = (column: LegislatorTableColumn) => {
    switch (column) {
      case 'checkbox':
        return (
          <StandardTableHeaderCell key="checkbox" isCheckbox>
            <input
              type="checkbox"
              checked={context.selectedLegislatorIds?.size === data.length && data.length > 0}
              onChange={() => {
                if (context.selectedLegislatorIds?.size === data.length) {
                  data.forEach(l => context.onSelectLegislator?.(getLegislatorId(l), false));
                } else {
                  data.forEach(l => context.onSelectLegislator?.(getLegislatorId(l), true));
                }
              }}
              className="rounded border-border-light-secondary text-blue-600 focus:ring-blue-500"
            />
          </StandardTableHeaderCell>
        );

      case 'name':
        return (
          <StandardTableHeaderCell
            key="name"
            sortable
            sortKey="name"
            currentSortKey={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            columnType="nowrap"
          >
            Name
          </StandardTableHeaderCell>
        );

      case 'chamber':
        return (
          <StandardTableHeaderCell
            key="chamber"
            sortable
            sortKey="chamber"
            currentSortKey={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            columnType="narrow"
          >
            Chamber
          </StandardTableHeaderCell>
        );

      case 'district':
        return (
          <StandardTableHeaderCell
            key="district"
            sortable
            sortKey="district"
            currentSortKey={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            columnType="narrow"
            responsiveHide="md"
          >
            District
          </StandardTableHeaderCell>
        );

      case 'party':
        return (
          <StandardTableHeaderCell
            key="party"
            sortable
            sortKey="party"
            currentSortKey={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            columnType="narrow"
          >
            Party
          </StandardTableHeaderCell>
        );

      case 'committees':
        return (
          <StandardTableHeaderCell 
            key="committees" 
            columnType="expand" 
            responsiveHide="lg"
          >
            Committees
          </StandardTableHeaderCell>
        );

      case 'contact':
        return (
          <StandardTableHeaderCell 
            key="contact" 
            columnType="fit" 
            responsiveHide="md"
          >
            Contact
          </StandardTableHeaderCell>
        );

      case 'position':
        return (
          <StandardTableHeaderCell key="position" columnType="fit">
            Position
          </StandardTableHeaderCell>
        );

      case 'notes':
        return (
          <StandardTableHeaderCell key="notes" columnType="fit" align="center">
            Notes
          </StandardTableHeaderCell>
        );

      case 'sponsored_bills':
        return (
          <StandardTableHeaderCell
            key="sponsored_bills"
            sortable
            sortKey="sponsored_bills_count"
            currentSortKey={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            columnType="fit"
            responsiveHide="md"
          >
            Bills
          </StandardTableHeaderCell>
        );

      case 'votes':
        return (
          <StandardTableHeaderCell key="votes" columnType="fit" responsiveHide="lg">
            Votes
          </StandardTableHeaderCell>
        );

      case 'actions':
        return (
          <StandardTableHeaderCell key="actions" columnType="actions">
            Actions
          </StandardTableHeaderCell>
        );

      case 'workspaces':
        return (
          <StandardTableHeaderCell key="workspaces" columnType="expand">
            Workspaces
          </StandardTableHeaderCell>
        );

      default:
        return null;
    }
  };

  // Render column cell
  const renderCell = (column: LegislatorTableColumn, legislator: T) => {
    const legislatorId = getLegislatorId(legislator);
    const tracked = legislator as TrackedLegislator;

    switch (column) {
      case 'checkbox':
        return (
          <StandardTableCell key="checkbox" isCheckbox>
            <input
              type="checkbox"
              checked={context.selectedLegislatorIds?.has(legislatorId)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                context.onSelectLegislator?.(legislatorId, e.target.checked);
              }}
              className="rounded border-border-light-secondary text-blue-600 focus:ring-blue-500"
            />
          </StandardTableCell>
        );

      case 'name': {
        // Data is normalized to camelCase
        const isPriority = tracked.isPriority;
        const partyForColor = legislator.partyAffiliation || '';
        const legislatorDetailUrl = getLegislatorLink(legislatorId, context);
        const currentUrl = location.pathname + location.search;
        return (
          <StandardTableCell key="name">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button 
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  navigate(legislatorDetailUrl, { state: { returnUrl: currentUrl } });
                }}
                className={getLegislatorLinkClasses(partyForColor)}
              >
                {formatLegislatorNameFromObject(legislator)}
              </button>
              {isPriority && (
                <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
              )}
            </div>
          </StandardTableCell>
        );
      }

      case 'chamber':
        return (
          <StandardTableCell key="chamber">
            {isReportView ? (
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {legislator.chamber}
              </span>
            ) : (
              <Chip 
                label={legislator.chamber} 
                variant={legislator.chamber === 'House' ? 'primary' : 'secondary'}
                size="sm"
              />
            )}
          </StandardTableCell>
        );

      case 'district':
        return (
          <StandardTableCell key="district" responsiveHide="md">
            {isReportView ? (
              <span className="text-text-secondary dark:text-text-dark-secondary">
                {padDistrictNumber(legislator.district)}
              </span>
            ) : (
              <DistrictMapLink
                chamber={legislator.chamber}
                district={legislator.district}
                className="text-sm text-text-secondary dark:text-text-dark-secondary"
              />
            )}
          </StandardTableCell>
        );

      case 'party': {
        // Data is normalized to camelCase
        const partyAffiliation = legislator.partyAffiliation || '';
        return (
          <StandardTableCell key="party">
            <span className={`text-sm whitespace-nowrap ${getPartyTextColor(partyAffiliation)}`}>
              {formatPartyDisplay(partyAffiliation)}
            </span>
          </StandardTableCell>
        );
      }

      case 'committees':
        return (
          <StandardTableCell key="committees" responsiveHide="lg">
            {isReportView ? (
              <TruncatedText 
                text={legislator.committees || 'None'} 
                lines={2}
                className="text-sm break-words"
              />
            ) : (
              <div className="break-words text-sm text-text-secondary dark:text-text-dark-secondary">
                {legislator.committees || 'None'}
              </div>
            )}
          </StandardTableCell>
        );

      case 'contact': {
        // Data is normalized to camelCase
        const phone = legislator.workPhone || '';
        const formattedPhone = formatPhoneNumber(phone);
        return (
          <StandardTableCell key="contact" responsiveHide="md">
            <div className="flex flex-col gap-1">
              {legislator.email && (
                <a 
                  href={`mailto:${legislator.email}`}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={legislator.email}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Mail className="w-3 h-3" />
                  <span className="truncate max-w-[150px]">{legislator.email}</span>
                </a>
              )}
              {phone && (
                <a 
                  href={`tel:${phone}`}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={formattedPhone}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Phone className="w-3 h-3" />
                  <span>{formattedPhone}</span>
                </a>
              )}
              {legislator.website && (
                <a 
                  href={legislator.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                  title={legislator.website}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Globe className="w-3 h-3" />
                  <span>Website</span>
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </StandardTableCell>
        );
      }

      case 'position': {
        const position = tracked.position || 'Neutral';
        return (
          <StandardTableCell key="position">
            {context.permissions?.canEdit ? (
              <select
                value={position}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  e.stopPropagation();
                  context.onPositionChange?.(legislatorId, e.target.value);
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                className={`text-xs px-2 py-1 border border-border-light dark:border-border-dark rounded-md font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${getPositionColorClasses(position)}`}
              >
                <option value="Support">Support</option>
                <option value="Oppose">Oppose</option>
                <option value="Monitor">Monitor</option>
                <option value="Neutral">Neutral</option>
              </select>
            ) : (
              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(position)}`}>
                {position}
              </span>
            )}
          </StandardTableCell>
        );
      }

      case 'notes':
        return (
          <StandardTableCell key="notes" align="center">
            {renderNotesCell ? renderNotesCell(legislator) : (
              <div className="flex items-center justify-center">
                <StickyNote className="w-4 h-4 text-text-tertiary" />
              </div>
            )}
          </StandardTableCell>
        );

      case 'sponsored_bills': {
        // Data is normalized to camelCase
        const billsCount = (legislator as TrackedLegislator).sponsoredBillsCount || 0;
        const sessionYear = legislator.sessionYear;
        // Use public links if requested (for reports), otherwise use authenticated bills page
        const usePublic = context.usePublicLinks || context.isPublicView;
        const billsUrl = usePublic 
          ? `/public/legislators/${legislatorId}`
          : createBillFilterUrl(String(legislatorId), undefined, { sessionYear });
        // Get current URL to pass as returnUrl for proper back navigation
        const currentUrl = location.pathname + location.search;
        return (
          <StandardTableCell key="sponsored_bills" responsiveHide="md">
            <button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                navigate(billsUrl, { state: { returnUrl: currentUrl } });
              }}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
            >
              <FileText className="w-4 h-4" />
              <span className="font-medium">{billsCount}</span>
            </button>
          </StandardTableCell>
        );
      }

      case 'votes':
        return (
          <StandardTableCell key="votes" responsiveHide="lg">
            <Link
              to={getLegislatorLink(legislatorId, context, 'voting-records')}
              className="flex items-center gap-1 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 text-sm"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <Vote className="w-4 h-4" />
              <span>View</span>
            </Link>
          </StandardTableCell>
        );

      case 'actions': {
        const isTracked = context.trackedLegislatorIds?.has(legislatorId);
        return (
          <StandardTableCell key="actions">
            <div className="flex items-center gap-2">
              {/* View Details */}
              <button 
                className="p-1 text-text-tertiary hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary transition-colors"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  context.onNavigate?.(legislatorId);
                }}
                title="View details"
              >
                <Eye className="w-4 h-4" />
              </button>

              {/* Bookmark (for page view) */}
              {context.onBookmark && (
                <button 
                  className={`p-1 transition-colors ${
                    isTracked 
                      ? 'text-blue-600 hover:text-blue-700' 
                      : 'text-text-tertiary hover:text-blue-600 dark:text-text-dark-tertiary'
                  }`}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    // Data is normalized to camelCase
                    context.onBookmark!(legislatorId, legislator.fullName || '');
                  }}
                  title={isTracked ? "Manage tracking" : "Track legislator"}
                >
                  <Bookmark className="w-4 h-4" fill={isTracked ? 'currentColor' : 'none'} />
                </button>
              )}

              {/* Priority Toggle (for workspace view) */}
              {context.onPriorityToggle && context.permissions?.canEdit && (
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    // Data is normalized to camelCase
                    const isPriority = tracked.isPriority;
                    context.onPriorityToggle!(legislatorId, !isPriority);
                  }}
                  className={`p-1 transition-colors ${
                    tracked.isPriority
                      ? 'text-yellow-500 hover:text-yellow-600' 
                      : 'text-text-tertiary dark:text-text-dark-tertiary hover:text-yellow-500'
                  }`}
                  title={tracked.isPriority ? 'Remove from priority' : 'Mark as priority'}
                >
                  <Star className="w-4 h-4" fill={tracked.isPriority ? 'currentColor' : 'none'} />
                </button>
              )}

              {/* Remove (for workspace view) */}
              {context.onRemove && context.permissions?.canEdit && (
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    context.onRemove!(tracked);
                  }}
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 transition-colors"
                  title="Stop tracking"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </StandardTableCell>
        );
      }

      case 'workspaces': {
        const wsArray = (legislator as any).workspaces as Array<{ name: string; id: string; position: string }> | undefined;
        return (
          <StandardTableCell key="workspaces">
            <WorkspaceBadgeList workspaces={wsArray ?? []} />
          </StandardTableCell>
        );
      }

      default:
        return null;
    }
  };

  return (
    <StandardTable loading={isLoading} variant={tableVariant}>
      <StandardTableHeader variant={tableVariant}>
        <tr>
          {columns.map(col => renderHeader(col))}
        </tr>
      </StandardTableHeader>
      <StandardTableBody>
        {data.map((legislator) => (
          <StandardTableRow 
            key={getLegislatorId(legislator)} 
            hoverable
            onClick={onRowClick ? () => onRowClick(legislator) : undefined}
          >
            {columns.map(col => renderCell(col, legislator))}
          </StandardTableRow>
        ))}
      </StandardTableBody>
    </StandardTable>
  );
}

export default LegislatorTable;
